
import {
  computed,
  ref,
  defineComponent,
} from "@nuxtjs/composition-api";
import { useElementSize } from "@vueuse/core";
import { omit } from "~/utils/helpers";
import { definePropType } from "~/utils/props";

type Item =
  | {
      type?: "item";
      label: string;
      value: string | number;
      name: string;
      id: string | number;
      children?: Array<{
        label: string;
        value: string | number;
        name: string;
      }>;
    }
  | {
      type: "title";
      label: string;
      id: string | number;
      value: string | number;
    };

export default defineComponent({
  name: "ProductFilterItem",
  props: {
    label: {
      type: String,
      default: "",
    },
    labelWdith: {
      type: String,
      default: "4.5em",
    },
    items: {
      type: definePropType<Item[]>(Array),
      default: () => [],
    },
    rowHeight: {
      type: Number,
      default: 40,
    },
  },
  setup(props) {
    const isHovering = ref(false);
    const hoverItemValue = ref<Item["value"]>("");

    const onItemMouseenter = (item: Item) => {
      isHovering.value = true;
      hoverItemValue.value = item.value;
    };
    const onItemMouseleave = () => {
      isHovering.value = false;
      hoverItemValue.value = "";
    };

    const itemRefs = ref<HTMLDivElement[]>([]);

    const contentRef = ref();

    const { width: contentWidth, height: contentHeight } =
      useElementSize(contentRef);

    const contentStyle = computed(() => {
      return {
        width: `${contentWidth.value}px`,
      };
    });

    const popoverStyle = computed(() => {
      const index = props.items.findIndex(
        (item) => item.value === hoverItemValue.value
      );
      if (!hoverItemValue || index < 0 || !itemRefs.value.length) {
        return "";
      }
      let el = itemRefs.value[index];
      let right_px = isOverflow.value ? '-100px' : '0'
      return {
        top: el ? `${el.offsetTop + el.offsetHeight}px` : 0,
        right: right_px
      };
    });

    const collapseBoxRef = ref<HTMLElement>();
    const { height: collapseBoxHeight } = useElementSize(collapseBoxRef);
    const appendBox = ref<HTMLElement>();
    const { width: appendBoxWidth } = useElementSize(appendBox);
    const maxWidth = computed(() => {
      return contentWidth.value - appendBoxWidth.value;
    });
    const collapseBoxStyle = computed(() => {
      return {
        maxWidth: maxWidth.value > 0 ? `${maxWidth.value}px` : "auto",
      };
    });

    const isOverflow = computed(() => {
      return itemRefs.value.some((item) => item.offsetTop >= props.rowHeight);
    });

    const expanded = ref(false);

    return {
      isHovering,
      hoverItemValue,
      onItemMouseenter,
      onItemMouseleave,

      itemRefs,

      contentRef,
      contentWidth,
      contentHeight,
      contentStyle,

      popoverStyle,

      collapseBoxRef,
      collapseBoxHeight,
      collapseBoxStyle,

      appendBox,
      appendBoxWidth,
      maxWidth,

      isOverflow,

      expanded,

      // resolveHref,
    };
  },
  render(createElement, hack) {
    const resolveHref = (query: Record<string, string>) => {
      return this.$router.resolve({
        query: omit({
          ...this.$route.query,
          ...query
        }, ['page_index']),
      }).href;
    };
    const labelStyle = {
      width: this.labelWdith,
    };

    const viewBoxStyle = {
      height: this.expanded ? "auto" : `${this.rowHeight}px`,
    };
    const renderItems = () => {
      return this.items.map((item, index) => {
        const isHovering = this.hoverItemValue === item.value;
        const hasChildren =
          item.type === "title"
            ? false
            : item?.children && item.children.length > 0;
        const titleEle = (
          <div class="text-muted px-2.5 py-2 text-base">{item.label}</div>
        );
        const contentNodes =
          item.type === "title"
            ? titleEle
            : [
                <a
                  target="_self"
                  class="flex items-center relative px-2.5 py-2 text-dark cursor-pointer hover:text-highlight"
                  href={resolveHref({
                    [item.name]: String(item.value),
                  })}
                  title={item.label}
                >
                  <div
                    class="absolute inset-0 border-t border-t-2 border-t-darker bg-white !border-t-[#1F2129] shadow pointer-events-none"
                    style={{
                      display: isHovering && hasChildren ? "block" : "none",
                    }}
                  />
                  <div class="relative">
                    <span class="mr-1">{item.label}</span>
                    {hasChildren ? (
                      <i class="el-icon-arrow-down text-sm" />
                    ) : null}
                  </div>
                </a>,

                hasChildren && isHovering ? (
                  <div
                    class="absolute z-1 bg-white top-full left-0 shadow"
                    style={this.popoverStyle}
                  >
                    <ul class="flex flex-wrap p-1" style={this.contentStyle}>
                      {item.children?.map((sec) => (
                        <li
                          key={sec.value}
                          class="px-2.5 py-1 text-dark cursor-pointer transition-colors hover:text-highlight active:bg-[#E5E7EB]"
                        >
                          <a
                            href={resolveHref({
                              [item.name]: String(item.value),
                              [sec.name]: String(sec.value),
                            })}
                            target="_self"
                            title={`${item.label}/${sec.label}`}
                          >
                            {sec.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null,
              ];
        //
        return (
          <div
            // @ts-ignore
            refInFor
            ref="itemRefs"
            key={item.id || index}
            onMouseenter={() => this.onItemMouseenter(item)}
            onMouseleave={this.onItemMouseleave}
          >
            {contentNodes}
          </div>
        );
      });
    };

    const appenEle = this.$scopedSlots.append ? (
      <div class="inline-flex self-center" ref="appendBox">
        {this.$scopedSlots.append({})}
      </div>
    ) : null;

    return (
      <div class="flex">
        <div
          class="text-base font-medium text-dark mr-6 py-2 flex-shrink-0"
          style={labelStyle}
        >
          {this.label}
        </div>

        {/* 中间内容区域 */}
        <div ref="contentRef" class="flex-1 relative">
          <div class="overflow-hidden flex" style={viewBoxStyle}>
            <div
              ref="collapseBoxRef"
              class="flex flex-wrap"
              style={!this.expanded ? this.collapseBoxStyle : ""}
            >
              {renderItems()}
              {this.expanded ? appenEle : null}
            </div>

            {!this.expanded ? appenEle : null}
          </div>
        </div>

        {/* 右侧操作 */}
        {this.isOverflow ? (
          <div class="ml-8 flex-shrink-0">
            <button
              class="border border-darker rounded-sm px-2 py-0.5 text-xs mt-2 bg-white text-dark"
              onClick={() => (this.expanded = !this.expanded)}
              type="button"
            >
              <span class="mr-1"> {this.expanded ? "收起" : "更多"} </span>
              <i
                class={
                  this.expanded ? "el-icon-arrow-up" : "el-icon-arrow-down"
                }
              />
            </button>
          </div>
        ) : null}
      </div>
    );
  },
});
